import * as React from "react";
import { Button, Text } from "@uikit";
import { FilterParam } from "@models/app";

import { useBreakpoint } from "use-breakpoint";
import { BREAKPOINTS } from "@utilities/constants";
import { Hotel, pluralize } from "@kalabo/golfsavers-common";

import { CheckCircleIcon } from "@heroicons/react/solid";
import { IoCloseOutline } from "@react-icons/all-files/io5/IoCloseOutline";
import { IoSwapVerticalOutline } from "@react-icons/all-files/io5/IoSwapVerticalOutline";
import { Popover, Transition } from "@headlessui/react";
import { useApp } from "@store/app";

interface IProps {
  package_count: number;
  hotel: Hotel;
}

const sort_options = [
  {
    label: "Price (High to Low)",
    value: "price_desc",
  },
  {
    label: "Price (Low to High)",
    value: "price_asc",
  },
];

const PackageSortBar = ({ package_count, hotel }: IProps) => {
  const search = useApp(($) => $.search);
  const removeHotelFilterByKey = useApp(($) => $.removeHotelFilterByKey);
  const resetPackageFilters = useApp(($) => $.resetPackageFilters);
  const resetHotelFilters = useApp(($) => $.resetHotelFilters);
  const setPackageSortField = useApp(($) => $.setPackageSortField);
  const { breakpoint } = useBreakpoint(BREAKPOINTS, "mobile");

  let active_sort = sort_options?.find(
    (option) => option.value === search?.package?.sort?.field
  );

  let isEmpty = search?.hotel?.filters.courses.length === 0;

  const getSortParams = () => {
    const items: React.JSX.Element[] = [];

    for (const item of search?.hotel?.filters?.courses) {
      items.push(
        <div
          onClick={() => {
            removeHotelFilterByKey({
              key: "courses",
              value: item,
            });
          }}
          className=" cursor-pointer border rounded-2xl text-13 px-[15px] py-[5px] inline-flex items-center justify-between gap-2 bg-grey border-grey3"
        >
          <span dangerouslySetInnerHTML={{ __html: item.name }} />

          <IoCloseOutline className="h-4 w-4 text-grey7" aria-hidden="true" />
        </div>
      );
    }

    return items;
  };

  return (
    <>
      <div className="sm:mb-2 pb-2 sm:pb-0 border-b border-b-grey3 sm:border-b-0">
        <div className="grid grid-cols-12">
          <div className="col-span-12 sm:col-span-12">
            <div className="flex items-top mb-[2px] pb-2 justify-between ">
              <Text
                sizeTablet={15}
                sizeDesktop={15}
                leading={1.3}
                size={13}
                element="h3"
                color="black90"
                className=""
              >
                {breakpoint === "mobile" ? (
                  <>Showing {package_count} results</>
                ) : (
                  <>
                    Showing{" "}
                    <span className="font-semibold">
                      {pluralize(
                        package_count,
                        "golf package",
                        "golf packages"
                      )}
                    </span>{" "}
                    at {hotel.name.en}
                  </>
                )}
              </Text>

              <Popover className="sm:hidden">
                {({ open, close }) => (
                  <>
                    <Popover.Button
                      className={
                        "flex items-center gap-1 focus:outline-none focus:ring-0"
                      }
                    >
                      <Text
                        sizeTablet={15}
                        sizeDesktop={15}
                        size={13}
                        leading={1.3}
                        weight="semibold"
                        color={"black90"}
                      >
                        {active_sort?.label}
                      </Text>
                      <IoSwapVerticalOutline className="h-[17px] mt-[1px] text-grey7" />
                    </Popover.Button>

                    <Transition
                      as={React.Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0 sm:translate-y-2"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel
                        className="fixed z-[1000] top-0 sm:top-auto bg-black/60 sm:bg-transparent items-end flex bottom-0 sm:bottom-auto left-0 right-0 sm:absolute sm:left-auto"
                        onClick={(e) => {
                          // if clicked directly on the panel, close the popover
                          if (e.target === e.currentTarget) {
                            close();
                          }
                        }}
                      >
                        <div className="overflow-hidden md:rounded-lg shadow-lg w-full  ">
                          <div className="shadow-modal border-[1px] border-grey3 flex flex-col  md:w-[415px] bg-white rounded-t-lg sm:rounded-lg  ">
                            <div className="sm:hidden border-b border-grey3 px-5 py-3 sm:py-8 sm:px-10 flex justify-end">
                              <button
                                type="button"
                                className="rounded-md inline-flex items-center justify-center text-gray-400"
                                onClick={(e) => {
                                  close();
                                }}
                              >
                                <span className="sr-only">Close menu</span>

                                <IoCloseOutline
                                  className="h-6 w-6 text-grey7"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                            <div className="flex w-full">
                              <div className="flex flex-col  pb-16 w-full">
                                {sort_options.map((option, index) => {
                                  return (
                                    <div
                                      key={index}
                                      className="px-5 py-4 border-b border-grey2 flex justify-between"
                                      onClick={() => {
                                        close();

                                        setPackageSortField(option.value);
                                      }}
                                    >
                                      {option.label}
                                      {active_sort?.value === option.value && (
                                        <CheckCircleIcon className="w-[26px] text-blue" />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
              <div className="gap-5 hidden lg:flex">
                <Text size={15} color="black90" className="pb-2">
                  Sort By:
                </Text>
                <div className="flex gap-5">
                  {sort_options.map((option, index) => {
                    let active = search?.package?.sort?.field === option.value;
                    return (
                      <div
                        key={index}
                        onClick={() => {
                          setPackageSortField(option.value);
                        }}
                        className={`border-b-2 cursor-pointer  ${
                          active ? "border-b-blue" : "border-b-transparent"
                        }`}
                      >
                        <Text
                          size={15}
                          weight="semibold"
                          color={active ? "black90" : "black40"}
                          className="pb-2"
                        >
                          {option.label}
                        </Text>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isEmpty && (
          <div className="flex pt-4 mb-5 justify-between gap-5 items-center ">
            <div className="flex flex-1 justify-between items-center bg-[#FDF6EC] border-t border-b border-grey2 p-2">
              <div className="flex gap-2  flex-wrap">{getSortParams()}</div>

              <Button
                onClick={async () => {
                  resetPackageFilters();
                  resetHotelFilters();
                }}
                size="small"
                invert
                color="blue"
                text="Remove filter"
                className="!m-0 bg-white border-grey3 !px-3 self-center whitespace-nowrap"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PackageSortBar;
